import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlay, FiYoutube, FiUsers, FiGlobe, FiTrendingUp, FiDollarSign, FiBarChart3, FiSettings, FiHeart, FiShield, FiTarget, FiMonitor } = FiIcons;

const Services = () => {
  const [activeService, setActiveService] = useState(0);

  const services = [
    {
      id: 'ott-streaming',
      icon: FiPlay,
      title: 'OTT & Streaming Channels',
      subtitle: 'Launch Your Own Streaming Platform',
      description: 'Break free from platform dependency with custom OTT channels for Roku, Fire TV, Apple TV, and web-based streaming. Perfect for LGBTQ+ creators who want complete control over their content distribution and monetization.',
      features: [
        'Custom Roku, Fire TV, and Apple TV channel development',
        'Web-based OTT platform creation with responsive design',
        'Advanced monetization including ads, subscriptions, and pay-per-view',
        'Real-time audience analytics and engagement tracking',
        'Content management systems optimized for LGBTQ+ creators',
        'Multi-platform distribution and cross-promotion strategies',
        'Brand-consistent channel design and user experience',
        'Technical support and platform maintenance'
      ],
      benefits: [
        'Own your audience and content completely',
        'Generate revenue through multiple monetization streams',
        'Build brand recognition across streaming platforms',
        'Reach cord-cutting audiences who prefer OTT content'
      ],
      cta: 'Launch Your Streaming Channel',
      seoKeywords: 'OTT channels LGBTQ creators NYC, streaming platform development, Roku Fire TV Apple TV channels, LGBTQ streaming content'
    },
    {
      id: 'youtube-management',
      icon: FiYoutube,
      title: 'YouTube Channel Management',
      subtitle: 'Optimize, Grow, and Monetize',
      description: 'Comprehensive YouTube channel management tailored specifically for LGBTQ+ content creators in NYC. From strategy development to daily management, we help you build sustainable growth and meaningful community engagement.',
      features: [
        'Channel optimization and SEO strategy for LGBTQ+ keywords',
        'Content planning and publishing workflows',
        'Thumbnail design and video optimization',
        'Community management and engagement strategies',
        'YouTube Shorts and live streaming optimization',
        'Monetization strategy including AdSense, memberships, and Super Chat',
        'Cross-platform promotion and integration',
        'Performance analytics and growth tracking'
      ],
      benefits: [
        'Increase subscriber growth by 300% on average',
        'Improve video discoverability and search rankings',
        'Build engaged communities around your content',
        'Maximize monetization opportunities across YouTube features'
      ],
      cta: 'Grow Your YouTube Channel',
      seoKeywords: 'YouTube management LGBTQ creators NYC, YouTube channel optimization, LGBTQ YouTube growth, NYC content creator services'
    },
    {
      id: 'social-media',
      icon: FiUsers,
      title: 'Social Media Management',
      subtitle: 'Multi-Platform Growth Strategy',
      description: 'Strategic social media management across Instagram, TikTok, Twitter, and emerging platforms. We create platform-specific content strategies that amplify your voice while building authentic connections with LGBTQ+ communities.',
      features: [
        'Platform-specific content strategy and creation',
        'Posting schedules optimized for LGBTQ+ audience engagement',
        'Community management and relationship building',
        'Influencer collaboration and partnership strategies',
        'Crisis management and reputation protection',
        'Cross-platform content adaptation and optimization',
        'Hashtag research and trend analysis',
        'Social media advertising and promotion campaigns'
      ],
      benefits: [
        'Build authentic connections with your target audience',
        'Increase engagement rates and follower quality',
        'Develop consistent brand voice across platforms',
        'Drive traffic to your primary content channels'
      ],
      cta: 'Amplify Your Social Presence',
      seoKeywords: 'social media management LGBTQ NYC, Instagram TikTok management, LGBTQ social media strategy, NYC queer content marketing'
    },
    {
      id: 'community-platforms',
      icon: FiGlobe,
      title: 'Community-Based Platforms',
      subtitle: 'Build Your Own Digital Community',
      description: 'Custom-built digital communities and membership platforms designed specifically for LGBTQ+ creators and niche audiences. Create safe, inclusive spaces where your community can connect, engage, and support your work directly.',
      features: [
        'Custom community platform development',
        'Membership and subscription management systems',
        'Forum and discussion board integration',
        'Event hosting and virtual meetup capabilities',
        'Direct creator-to-fan messaging and interaction tools',
        'Exclusive content delivery and access controls',
        'Community moderation tools and safety features',
        'Integration with existing social media and content channels'
      ],
      benefits: [
        'Create recurring revenue through memberships',
        'Build deeper relationships with your most engaged fans',
        'Own your community data and communication channels',
        'Provide exclusive value that keeps supporters coming back'
      ],
      cta: 'Build Your Community Platform',
      seoKeywords: 'LGBTQ community platforms NYC, custom digital communities, membership platforms queer creators, community-driven media LGBTQ'
    }
  ];

  return (
    <section id="services" className="py-20 lg:py-32 bg-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="text-white">Comprehensive</span>
            <br />
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Creator Services
            </span>
          </h2>
          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            From OTT channel development to community platform creation, we provide end-to-end media 
            services designed specifically for LGBTQ+ content creators and niche communities in NYC and beyond.
          </p>
        </motion.div>

        {/* Service Navigation */}
        <motion.div
          className="flex flex-wrap justify-center mb-12 gap-4"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
        >
          {services.map((service, index) => (
            <motion.button
              key={service.id}
              className={`flex items-center space-x-2 px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                activeService === index
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
              onClick={() => setActiveService(index)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <SafeIcon icon={service.icon} />
              <span className="hidden sm:inline">{service.title}</span>
            </motion.button>
          ))}
        </motion.div>

        {/* Active Service Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeService}
            className="bg-gray-900 rounded-3xl overflow-hidden"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.5 }}
          >
            <div className="p-8 lg:p-12">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                {/* Content */}
                <div>
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center">
                      <SafeIcon icon={services[activeService].icon} className="text-2xl text-white" />
                    </div>
                    <div>
                      <h3 className="text-3xl font-bold text-white mb-2">{services[activeService].title}</h3>
                      <p className="text-purple-400 font-semibold">{services[activeService].subtitle}</p>
                    </div>
                  </div>

                  <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                    {services[activeService].description}
                  </p>

                  <motion.button
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 px-8 py-4 rounded-full font-semibold text-lg shadow-lg hover:shadow-purple-500/25 transition-all duration-200 flex items-center space-x-2"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span>{services[activeService].cta}</span>
                    <SafeIcon icon={FiTrendingUp} />
                  </motion.button>
                </div>

                {/* Features */}
                <div>
                  <h4 className="text-2xl font-bold text-white mb-6">What's Included</h4>
                  <div className="space-y-4 mb-8">
                    {services[activeService].features.map((feature, index) => (
                      <motion.div
                        key={feature}
                        className="flex items-start space-x-3"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <SafeIcon icon={FiTarget} className="text-purple-400 mt-1 flex-shrink-0" />
                        <span className="text-gray-300">{feature}</span>
                      </motion.div>
                    ))}
                  </div>

                  <h4 className="text-xl font-bold text-white mb-4">Key Benefits</h4>
                  <div className="space-y-3">
                    {services[activeService].benefits.map((benefit, index) => (
                      <motion.div
                        key={benefit}
                        className="flex items-start space-x-3"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: (services[activeService].features.length + index) * 0.1 }}
                      >
                        <SafeIcon icon={FiHeart} className="text-pink-400 mt-1 flex-shrink-0" />
                        <span className="text-gray-300 font-medium">{benefit}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Service Highlights */}
        <motion.div
          className="mt-20 grid md:grid-cols-3 gap-8"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <div className="text-center bg-gray-900 rounded-2xl p-8 border border-gray-700">
            <SafeIcon icon={FiShield} className="text-4xl text-purple-400 mx-auto mb-4" />
            <h4 className="text-xl font-bold text-white mb-4">Safe & Inclusive</h4>
            <p className="text-gray-300">
              Every platform and strategy we develop prioritizes LGBTQ+ safety, authentic representation, and community well-being.
            </p>
          </div>

          <div className="text-center bg-gray-900 rounded-2xl p-8 border border-gray-700">
            <SafeIcon icon={FiDollarSign} className="text-4xl text-pink-400 mx-auto mb-4" />
            <h4 className="text-xl font-bold text-white mb-4">Revenue-Focused</h4>
            <p className="text-gray-300">
              We don't just build platforms – we create sustainable monetization strategies that help creators earn from their passion.
            </p>
          </div>

          <div className="text-center bg-gray-900 rounded-2xl p-8 border border-gray-700">
            <SafeIcon icon={FiBarChart3} className="text-4xl text-purple-400 mx-auto mb-4" />
            <h4 className="text-xl font-bold text-white mb-4">Data-Driven Growth</h4>
            <p className="text-gray-300">
              Our strategies are backed by analytics and insights specific to LGBTQ+ audiences and NYC market dynamics.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;